import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import MarketingLayout from '@/components/MarketingLayout';
import { Section, Container } from '@/components/shared/Section';
import { GlassPanel } from '@/components/shared/GlassPanel';
import { ProductFrame } from '@/components/shared/ProductFrame';
import { SignalChip } from '@/components/shared/SignalChip';
import { MetricCard } from '@/components/shared/MetricCard';
import { AnimatedReveal } from '@/components/shared/AnimatedReveal';
import { AnimatedChart } from '@/components/shared/AnimatedChart';
import { SEO, organizationSchema, faqSchema } from '@/components/shared/SEO';
import { fadeInUp, staggerContainer, viewportOnce } from '@/lib/motion';
import { PromoSpotsDisplay } from '@/components/PromoSpotsDisplay';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Eye, TrendingUp, Bell, Search, BarChart3, ShieldCheck,
  ArrowRight, Zap, Target, Globe, DollarSign, Package,
  Monitor, Smartphone, Check, Star, ChevronRight, Activity
} from 'lucide-react';

/* ─── HERO ─── */
const HeroSection = () => {
  const { scrollY } = useScroll();
  const glowY = useTransform(scrollY, [0, 500], [0, 24]);

  return (
    <Section hero className="relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 radar-glow-hero" />
      <div className="absolute inset-0 radar-grid opacity-40" />
      <motion.div
        className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full bg-lime-500/[0.04] blur-[120px]"
        style={{ y: glowY }}
      />

      <Container className="relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left - Copy */}
          <motion.div variants={staggerContainer} initial="hidden" animate="visible">
            <motion.div variants={fadeInUp} className="flex items-center gap-3 mb-6">
              <SignalChip variant="lime">Daily updates</SignalChip>
              <SignalChip>Meta &middot; Google</SignalChip>
            </motion.div>

            <motion.h1 variants={fadeInUp} className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold tracking-tight text-foreground leading-[1.08] mb-6">
              See what ads actually work{' '}
              <span className="text-[#CCFF6C]">for your competitors</span>
            </motion.h1>

            <motion.p variants={fadeInUp} className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-[520px] mb-8">
              Track competitor ads and products daily. Know which creative angles survive and what products they're pushing — automatically.
            </motion.p>

            <motion.div variants={fadeInUp} className="flex flex-wrap items-center gap-4 mb-10">
              <Link
                to="/waitlist"
                className="inline-flex flex-col items-start gap-0.5 px-7 py-3 rounded-xl bg-[#CCFF6C] text-black hover:bg-[#D9FF9A] transition-colors shadow-[0_0_30px_rgba(204,255,108,0.2)]"
                data-testid="hero-primary-cta"
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold">Get $99 Pro Plan for $49/mo</span>
                  <ArrowRight className="h-4 w-4" />
                </div>
                <PromoSpotsDisplay variant="hero" />
              </Link>
              <Link
                to="/solutions"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl border border-white/10 bg-white/[0.03] text-sm font-medium text-foreground hover:bg-white/[0.06] transition-colors"
                data-testid="hero-secondary-cta"
              >
                See how it works
              </Link>
            </motion.div>

            {/* Value proposition */}
            <motion.div variants={fadeInUp} className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/10">
                  <p className="text-[10px] text-muted-foreground">Basic Plan</p>
                  <p className="text-sm font-semibold text-foreground">$49/mo</p>
                  <p className="text-[10px] text-muted-foreground">5 competitors</p>
                </div>
                <span className="text-lime-400">→</span>
                <div className="px-3 py-1.5 rounded-lg bg-lime-500/10 border border-lime-500/20">
                  <p className="text-[10px] text-lime-400">Pro Plan (You get this!)</p>
                  <p className="text-sm font-semibold text-foreground"><s className="text-muted-foreground">$99</s> $49/mo</p>
                  <p className="text-[10px] text-lime-400">5 competitors + AI feedback on YOUR ads</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-lime-500" /> 5 competitors</span>
                <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-lime-500" /> AI feedback on YOUR ads</span>
                <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-lime-500" /> Priority support</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Right - Product Frame */}
          <motion.div
            initial={{ opacity: 0, x: 30, scale: 0.96 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <ProductFrame title="radar — competitor dashboard">
              <div className="p-4 sm:p-6 space-y-4">
                {/* Top bar */}
                <div className="flex items-center justify-between">
                  <div className="flex gap-2">
                    <span className="px-3 py-1 text-xs font-mono rounded-full bg-lime-500/10 text-lime-400 border border-lime-500/20">All Channels</span>
                    <span className="px-3 py-1 text-xs font-mono rounded-full bg-white/[0.04] text-muted-foreground border border-white/10">Meta</span>
                    <span className="px-3 py-1 text-xs font-mono rounded-full bg-white/[0.04] text-muted-foreground border border-white/10">Google</span>
                  </div>
                  <span className="text-xs font-mono text-muted-foreground">Last 7 days</span>
                </div>

                {/* Metrics row */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
                    <p className="text-[10px] font-mono uppercase text-muted-foreground mb-1">New Ads</p>
                    <p className="text-xl font-display font-semibold text-foreground">247</p>
                    <p className="text-[10px] text-[#7CFFB2] font-mono">+18% vs last week</p>
                  </div>
                  <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
                    <p className="text-[10px] font-mono uppercase text-muted-foreground mb-1">Price Changes</p>
                    <p className="text-xl font-display font-semibold text-foreground">34</p>
                    <p className="text-[10px] text-[#FFD38A] font-mono">8 increases, 26 drops</p>
                  </div>
                  <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
                    <p className="text-[10px] font-mono uppercase text-muted-foreground mb-1">Signals</p>
                    <p className="text-xl font-display font-semibold text-foreground">89</p>
                    <p className="text-[10px] text-[#7AD7FF] font-mono">5 high priority</p>
                  </div>
                </div>

                {/* Activity feed */}
                <div className="space-y-2">
                  {[
                    { time: '2m ago', msg: 'BrandX launched 3 new Meta video ads', type: 'lime' },
                    { time: '14m ago', msg: 'CompetitorY changed prices on 5 products', type: 'amber' },
                    { time: '1h ago', msg: 'RivalZ added new product collection', type: 'cyan' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3 rounded-lg border border-white/[0.04] bg-white/[0.01] px-3 py-2.5">
                      <div className={`mt-1.5 h-1.5 w-1.5 rounded-full flex-shrink-0 ${
                        item.type === 'lime' ? 'bg-lime-500 pulse-lime' :
                        item.type === 'amber' ? 'bg-[#FFD38A]' : 'bg-[#7AD7FF]'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-foreground/80 truncate">{item.msg}</p>
                        <p className="text-[10px] font-mono text-muted-foreground mt-0.5">{item.time}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </ProductFrame>
          </motion.div>
        </div>
      </Container>
    </Section>
  );
};

/* ─── WHAT RADAR DOES ─── */
const pillars = [
  {
    icon: Eye,
    title: 'Track competitor ads',
    desc: 'See every ad your competitors run on Meta and Google. We identify winning ads using cluster survival analysis — when multiple variants launch together, we show you which ones survive.',
    metric: 'Daily',
    metricLabel: 'Automatic sync',
  },
  {
    icon: DollarSign,
    title: 'Monitor product changes',
    desc: 'Track competitor product catalogs for Shopify stores. See price changes, new launches, and discount patterns as they happen.',
    metric: '24hr',
    metricLabel: 'Update frequency',
  },
  {
    icon: Activity,
    title: 'Track store changes & signals',
    desc: 'Detect theme updates, app installs, and tech changes. Plus get alerts for new ads, campaign signals, and confirmed winning ads (7-14 days).',
    metric: '150+',
    metricLabel: 'Signal types',
  },
];

const WhatRadarDoes = () => (
  <Section id="what-radar-does">
    <Container>
      <AnimatedReveal>
        <p className="text-xs font-mono uppercase tracking-wider text-lime-500 mb-3">What RADAR does</p>
        <h2 className="text-2xl md:text-3xl font-display font-bold tracking-tight text-foreground mb-4">
          Complete competitor intelligence in one place
        </h2>
        <p className="text-base text-muted-foreground max-w-[560px] mb-12">
          Stop manually checking competitor sites and ad libraries. RADAR automatically tracks ads and products so you know what's working.
        </p>
      </AnimatedReveal>

      <motion.div
        className="grid md:grid-cols-3 gap-6"
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
      >
        {pillars.map((p, i) => (
          <motion.div key={i} variants={fadeInUp}>
            <GlassPanel hover className="p-6 sm:p-7 h-full flex flex-col">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-lime-500/10 border border-lime-500/20">
                  <p.icon className="h-5 w-5 text-lime-500" />
                </div>
              </div>
              <h3 className="text-lg font-display font-semibold text-foreground mb-2">{p.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed flex-1">{p.desc}</p>
              <div className="mt-6 pt-4 border-t border-white/[0.06]">
                <span className="text-2xl font-display font-bold text-foreground">{p.metric}</span>
                <span className="block text-xs font-mono text-muted-foreground mt-1">{p.metricLabel}</span>
              </div>
            </GlassPanel>
          </motion.div>
        ))}
      </motion.div>
    </Container>
  </Section>
);

/* ─── WHY MERCHANTS NEED IT ─── */
const WhyMerchants = () => (
  <Section id="why-radar" className="border-y border-white/[0.04]">
    <Container>
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <AnimatedReveal>
          <p className="text-xs font-mono uppercase tracking-wider text-lime-500 mb-3">The problem</p>
          <h2 className="text-2xl md:text-3xl font-display font-bold tracking-tight text-foreground mb-6">
            Your competitors are moving fast. Are you watching?
          </h2>
          <div className="space-y-5">
            {[
              'Competitors launch new ads every day — across multiple channels.',
              'Pricing wars happen in hours, not weeks.',
              'Tech and storefront changes signal strategic moves.',
              'Without real-time intelligence, you are always reacting, never leading.',
            ].map((text, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="mt-1 h-5 w-5 flex items-center justify-center rounded-full bg-white/[0.06] border border-white/10 flex-shrink-0">
                  <span className="text-xs font-mono text-muted-foreground">{i + 1}</span>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </AnimatedReveal>

        <AnimatedReveal delay={0.2}>
          <GlassPanel className="p-6 sm:p-8">
            <p className="text-xs font-mono uppercase tracking-wider text-lime-500 mb-4">Intel brief</p>
            <div className="space-y-4">
              {[
                { label: 'New ads this week', value: '47', change: 'Meta & Google', trend: 'up' },
                { label: 'Price changes detected', value: '23', change: '8 drops', trend: 'up' },
                { label: 'Products added', value: '12', change: '3 collections', trend: 'down' },
                { label: 'Winning ads found', value: '3', change: 'High confidence', trend: 'up' },
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-between py-3 border-b border-white/[0.04] last:border-0">
                  <div>
                    <p className="text-xs text-muted-foreground">{item.label}</p>
                    <p className="text-lg font-display font-semibold text-foreground mt-0.5">{item.value}</p>
                  </div>
                  <span className={`text-xs font-mono px-2 py-1 rounded-full ${
                    item.trend === 'up' ? 'text-[#7CFFB2] bg-[#7CFFB2]/10' : 'text-[#FF6B6B] bg-[#FF6B6B]/10'
                  }`}>
                    {item.trend === 'up' ? '+' : ''}{item.change}
                  </span>
                </div>
              ))}
            </div>
          </GlassPanel>
        </AnimatedReveal>
      </div>
    </Container>
  </Section>
);

/* ─── CAPABILITY BREAKDOWN (TABS) ─── */
const capabilities = [
  {
    id: 'ads',
    label: 'Ads Monitoring',
    icon: Eye,
    title: 'Watch every competitor ad automatically',
    desc: 'See competitor creatives and copy across Meta and Google. Our winner detection identifies which ads survive when multiple variants launch together.',
    features: ['Video and image ad tracking', 'Copy and CTA analysis', 'Winning ad detection (7-14 days)', 'Campaign signal alerts', 'Daily sync updates'],
    mockContent: (
      <div className="p-4 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          {['Meta Video Ad - Summer Sale', 'Meta Carousel - New Launch', 'Google Search Ad - Brand Terms', 'Meta Story Ad - Flash Deal'].map((ad, i) => (
            <div key={i} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
              <div className="h-20 rounded-md bg-gradient-to-br from-white/[0.04] to-transparent mb-2 flex items-center justify-center">
                <Monitor className="h-6 w-6 text-muted-foreground/40" />
              </div>
              <p className="text-[11px] text-foreground/80 truncate">{ad}</p>
              <p className="text-[10px] font-mono text-muted-foreground mt-1">Detected 2h ago</p>
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    id: 'pricing',
    label: 'Pricing & Stock',
    icon: DollarSign,
    title: 'Track product and price changes daily',
    desc: 'Monitor competitor product catalogs on Shopify stores. See price changes, new launches, and discount activations as they happen.',
    features: ['Daily price tracking', 'New product detection', 'Discount alerts', 'Price change history', 'Product catalog monitoring'],
    mockContent: (
      <div className="p-4 space-y-3">
        {[
          { product: 'Premium Hoodie — Black', was: '$89', now: '$69', change: '-22%' },
          { product: 'Running Shoes V3', was: '$139', now: '$139', change: '0%' },
          { product: 'Wireless Earbuds Pro', was: '$59', now: '$44', change: '-25%' },
        ].map((item, i) => (
          <div key={i} className="flex items-center justify-between rounded-lg border border-white/[0.06] bg-white/[0.02] px-4 py-3">
            <div>
              <p className="text-xs text-foreground/80">{item.product}</p>
              <p className="text-[10px] font-mono text-muted-foreground mt-0.5">{item.was} → {item.now}</p>
            </div>
            <span className={`text-xs font-mono px-2 py-1 rounded-full ${
              item.change === '0%' ? 'text-muted-foreground bg-white/[0.04]' : 'text-[#FFD38A] bg-[#FFD38A]/10'
            }`}>{item.change}</span>
          </div>
        ))}
      </div>
    ),
  },
  {
    id: 'signals',
    label: 'Tech Signals',
    icon: Activity,
    title: 'Detect strategic shifts in competitor stores',
    desc: 'Track theme changes, app installs, and technology updates that reveal competitor strategy and priorities.',
    features: ['Theme change detection', 'App install tracking', 'Payment method updates', 'Technology stack analysis', 'Change timeline'],
    mockContent: (
      <div className="p-4 space-y-2">
        {[
          { signal: 'New theme deployed: Dawn 12.0', badge: 'THEME', color: 'cyan' },
          { signal: 'Installed: Klaviyo email marketing', badge: 'APP', color: 'lime' },
          { signal: 'Payment update: Added Shop Pay', badge: 'CHECKOUT', color: 'amber' },
          { signal: 'Added: Facebook Pixel tracking', badge: 'TRACKING', color: 'lime' },
        ].map((item, i) => (
          <div key={i} className="flex items-center gap-3 rounded-lg border border-white/[0.04] bg-white/[0.01] px-3 py-2.5">
            <SignalChip variant={item.color} className="text-[9px]">{item.badge}</SignalChip>
            <p className="text-xs text-foreground/80 flex-1">{item.signal}</p>
          </div>
        ))}
      </div>
    ),
  },
  {
    id: 'alerts',
    label: 'Alerts',
    icon: Bell,
    title: 'Get notified when it matters',
    desc: 'Receive intelligent alerts in-app and by email when competitors make significant moves. Never miss a pricing change, new campaign, or strategic shift.',
    features: ['In-app notifications', 'Email digests', 'Custom alert rules', 'Priority filtering', 'Instant push alerts'],
    mockContent: (
      <div className="p-4 space-y-2">
        {[
          { msg: 'BrandX changed prices on 12 products', time: 'Just now', priority: 'high' },
          { msg: 'CompetitorY launched new Meta campaign', time: '5 min ago', priority: 'medium' },
          { msg: 'RivalZ added 5 new products', time: '1 hour ago', priority: 'low' },
        ].map((item, i) => (
          <div key={i} className="flex items-start gap-3 rounded-lg border border-white/[0.04] bg-white/[0.01] px-3 py-3">
            <div className={`mt-1 h-2 w-2 rounded-full flex-shrink-0 ${
              item.priority === 'high' ? 'bg-[#FF6B6B] pulse-lime' :
              item.priority === 'medium' ? 'bg-[#FFD38A]' : 'bg-muted-foreground/40'
            }`} />
            <div className="flex-1">
              <p className="text-xs text-foreground/80">{item.msg}</p>
              <p className="text-[10px] font-mono text-muted-foreground mt-1">{item.time}</p>
            </div>
          </div>
        ))}
      </div>
    ),
  },
];

const CapabilityBreakdown = () => {
  const [activeTab, setActiveTab] = useState('ads');
  const active = capabilities.find(c => c.id === activeTab);

  return (
    <Section id="capabilities">
      <Container>
        <AnimatedReveal>
          <p className="text-xs font-mono uppercase tracking-wider text-lime-500 mb-3">Capabilities</p>
          <h2 className="text-2xl md:text-3xl font-display font-bold tracking-tight text-foreground mb-12">
            Everything you need to monitor the competition
          </h2>
        </AnimatedReveal>

        {/* Tabs */}
        <AnimatedReveal delay={0.1}>
          <div className="flex flex-wrap gap-2 mb-10" data-testid="capability-tabs">
            {capabilities.map((c) => (
              <button
                key={c.id}
                onClick={() => setActiveTab(c.id)}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  activeTab === c.id
                    ? 'bg-lime-500/10 text-lime-400 border border-lime-500/20'
                    : 'bg-white/[0.03] text-muted-foreground border border-white/10 hover:bg-white/[0.06]'
                }`}
                data-testid={`capability-tab-${c.id}`}
              >
                <c.icon className="h-4 w-4" />
                {c.label}
              </button>
            ))}
          </div>
        </AnimatedReveal>

        {/* Tab content */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          <AnimatedReveal key={active.id}>
            <h3 className="text-xl md:text-2xl font-display font-bold text-foreground mb-4">{active.title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-8">{active.desc}</p>
            <ul className="space-y-3">
              {active.features.map((f, i) => (
                <li key={i} className="flex items-center gap-3 text-sm text-foreground/80">
                  <Check className="h-4 w-4 text-lime-500 flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </AnimatedReveal>

          <AnimatedReveal delay={0.2}>
            <ProductFrame title={`radar — ${active.label.toLowerCase()}`}>
              {active.mockContent}
            </ProductFrame>
          </AnimatedReveal>
        </div>
      </Container>
    </Section>
  );
};

/* ─── CROSS-CHANNEL VISIBILITY ─── */
const channels = [
  { name: 'Meta Ads', desc: 'Facebook & Instagram ad monitoring', icon: Globe, count: 'Daily sync', accent: 'from-blue-500/10 to-transparent' },
  { name: 'Google Ads', desc: 'Search & display ad intelligence', icon: Search, count: 'Daily sync', accent: 'from-yellow-500/10 to-transparent' },
  { name: 'Product Tracking', desc: 'Shopify product catalog monitoring', icon: Package, count: 'Daily updates', accent: 'from-green-500/10 to-transparent' },
  { name: 'Price Changes', desc: 'Price drops, increases & discounts', icon: TrendingUp, count: 'Daily detection', accent: 'from-purple-500/10 to-transparent' },
  { name: 'Winner Detection', desc: 'Identify ads that survive testing', icon: Activity, count: '7-14 day analysis', accent: 'from-pink-500/10 to-transparent' },
];

const CrossChannel = () => (
  <Section className="border-y border-white/[0.04]">
    <Container>
      <AnimatedReveal>
        <p className="text-xs font-mono uppercase tracking-wider text-lime-500 mb-3">Cross-channel visibility</p>
        <h2 className="text-2xl md:text-3xl font-display font-bold tracking-tight text-foreground mb-4">
          One platform. Every channel covered.
        </h2>
        <p className="text-base text-muted-foreground max-w-[520px] mb-12">
          Stop manually checking ad libraries and competitor sites. RADAR monitors Meta, Google, and Shopify stores automatically.
        </p>
      </AnimatedReveal>

      <motion.div
        className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
      >
        {channels.map((ch, i) => (
          <motion.div key={i} variants={fadeInUp}>
            <GlassPanel hover className="p-6 h-full">
              <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${ch.accent} border border-white/10 flex items-center justify-center mb-4`}>
                <ch.icon className="h-5 w-5 text-foreground/70" />
              </div>
              <h3 className="text-base font-display font-semibold text-foreground mb-1.5">{ch.name}</h3>
              <p className="text-sm text-muted-foreground mb-4">{ch.desc}</p>
              <p className="text-xs font-mono text-lime-400/80">{ch.count}</p>
            </GlassPanel>
          </motion.div>
        ))}
      </motion.div>
    </Container>
  </Section>
);

/* ─── PRODUCT SHOWCASE ─── */
const ProductShowcase = () => (
  <Section>
    <Container>
      <AnimatedReveal>
        <div className="text-center mb-16">
          <p className="text-xs font-mono uppercase tracking-wider text-lime-500 mb-3">Product</p>
          <h2 className="text-2xl md:text-3xl font-display font-bold tracking-tight text-foreground mb-4">
            Built for operators who need answers fast
          </h2>
          <p className="text-base text-muted-foreground max-w-[520px] mx-auto">
            Every surface in RADAR is designed to surface intelligence quickly and clearly. No clutter. No noise. Just the signals that matter.
          </p>
        </div>
      </AnimatedReveal>

      <AnimatedReveal delay={0.15}>
        <ProductFrame title="radar — intelligence workspace" className="max-w-4xl mx-auto">
          <div className="p-6">
            {/* Top stats */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              {[
                { label: 'Competitors', val: '12', sub: 'Tracking daily' },
                { label: 'New Ads', val: '47', sub: 'This week' },
                { label: 'Price Changes', val: '23', sub: '8 drops' },
                { label: 'Winners Found', val: '3', sub: 'High confidence' },
              ].map((s, i) => (
                <div key={i} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3 text-center">
                  <p className="text-[10px] font-mono uppercase text-muted-foreground">{s.label}</p>
                  <p className="text-lg font-display font-bold text-foreground mt-1">{s.val}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">{s.sub}</p>
                </div>
              ))}
            </div>

            {/* Animated Chart */}
            <AnimatedChart />

            {/* Bottom feed */}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
                <p className="text-[10px] font-mono uppercase text-muted-foreground mb-2">Latest Signals</p>
                {['New Meta campaign detected', 'Price drop on 4 items', 'New product collection'].map((s, i) => (
                  <p key={i} className="text-[11px] text-foreground/70 py-1 border-b border-white/[0.03] last:border-0">{s}</p>
                ))}
              </div>
              <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
                <p className="text-[10px] font-mono uppercase text-muted-foreground mb-2">Top Movers</p>
                {['BrandX — 12 new ads', 'ShopY — Price changes', 'StoreZ — New products'].map((s, i) => (
                  <p key={i} className="text-[11px] text-foreground/70 py-1 border-b border-white/[0.03] last:border-0">{s}</p>
                ))}
              </div>
            </div>
          </div>
        </ProductFrame>
      </AnimatedReveal>
    </Container>
  </Section>
);

/* ─── HOW IT WORKS ─── */
const steps = [
  { num: '01', title: 'Install and set your niche', desc: 'Install RADAR from Shopify. We auto-discover competitor candidates based on your store profile. You approve which ones to track.' },
  { num: '02', title: 'Daily automatic monitoring', desc: 'RADAR syncs competitor ads from Meta and Google daily. For Shopify competitors, we also track their product catalog and price changes.' },
  { num: '03', title: 'See what actually works', desc: 'Get alerts for new ads and winning creatives. Winners are confirmed 7-14 days after launch when we see which variants survive.' },
];

const HowItWorks = () => (
  <Section id="how-it-works" className="border-y border-white/[0.04]">
    <Container>
      <AnimatedReveal>
        <p className="text-xs font-mono uppercase tracking-wider text-lime-500 mb-3">How it works</p>
        <h2 className="text-2xl md:text-3xl font-display font-bold tracking-tight text-foreground mb-12">
          Three steps to competitive advantage
        </h2>
      </AnimatedReveal>

      <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
        {steps.map((step, i) => (
          <AnimatedReveal key={i} delay={i * 0.1}>
            <div className="relative">
              <span className="text-5xl font-display font-bold text-white/[0.04] absolute -top-4 -left-2">{step.num}</span>
              <div className="relative pt-8">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-8 w-8 rounded-full bg-lime-500/10 border border-lime-500/20 flex items-center justify-center">
                    <span className="text-xs font-mono font-semibold text-lime-400">{step.num}</span>
                  </div>
                  {i < 2 && <div className="hidden md:block flex-1 h-px bg-gradient-to-r from-lime-500/20 to-transparent" />}
                </div>
                <h3 className="text-lg font-display font-semibold text-foreground mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
              </div>
            </div>
          </AnimatedReveal>
        ))}
      </div>
    </Container>
  </Section>
);

/* ─── SOLUTIONS BY CHANNEL ─── */
const solutionCards = [
  { title: 'Meta Ads Intelligence', desc: 'Monitor Facebook & Instagram ads. See what creative angles survive when competitors test multiple variants.', href: '/solutions/meta-ads', badge: 'Meta' },
  { title: 'Google Ads Intelligence', desc: 'Track search and display ads across Google Ads Transparency Center.', href: '/solutions/google-ads', badge: 'Google' },
  { title: 'Product Intelligence', desc: 'Monitor Shopify competitor catalogs. Track price changes, new launches, and discount patterns.', href: '/solutions/product-intel', badge: 'Products' },
];

const SolutionsByChannel = () => (
  <Section>
    <Container>
      <AnimatedReveal>
        <p className="text-xs font-mono uppercase tracking-wider text-lime-500 mb-3">Solutions</p>
        <h2 className="text-2xl md:text-3xl font-display font-bold tracking-tight text-foreground mb-4">
          Intelligence by channel
        </h2>
        <p className="text-base text-muted-foreground max-w-[520px] mb-12">
          Deep competitor monitoring across every advertising platform that matters for e-commerce.
        </p>
      </AnimatedReveal>

      <motion.div
        className="grid md:grid-cols-3 gap-6"
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        data-testid="solutions-by-channel"
      >
        {solutionCards.map((card, i) => (
          <motion.div key={i} variants={fadeInUp}>
            <Link to={card.href}>
              <GlassPanel hover className="p-6 sm:p-7 h-full flex flex-col group">
                <SignalChip variant="lime" className="self-start mb-4">{card.badge}</SignalChip>
                <h3 className="text-lg font-display font-semibold text-foreground mb-2">{card.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed flex-1">{card.desc}</p>
                <div className="mt-6 flex items-center gap-2 text-sm font-medium text-lime-400 group-hover:gap-3 transition-all">
                  Learn more <ChevronRight className="h-4 w-4" />
                </div>
              </GlassPanel>
            </Link>
          </motion.div>
        ))}
      </motion.div>
    </Container>
  </Section>
);

/* ─── PRICING ─── */
const plans = [
  {
    name: 'RADAR Pro',
    price: '$49',
    originalPrice: '$99',
    period: '/month',
    desc: 'Get the Pro plan at Basic pricing. Waitlist members only.',
    features: [
      'Track 5 competitors',
      'Meta & Google ad monitoring',
      'Winning ad detection (7-14 day analysis)',
      'Product catalog tracking (Shopify stores)',
      'Price change alerts',
      'Daily automatic sync',
      'AI feedback on YOUR ads (Pro only)',
      'Priority support (Pro only)',
      'Advanced analytics (Pro only)'
    ],
    bonuses: [
      '🔥 Early access to TikTok ads tracking',
      '🔥 First to connect YOUR ad accounts for AI feedback',
      '🔥 Direct performance insights on YOUR campaigns',
      '🔥 Priority access to influencer monitoring platform',
      '🔥 Pro plan features at Basic plan pricing forever'
    ],
    cta: 'Get Pro at Basic price — Join waitlist',
    highlighted: true,
    badge: 'PRO FOR $49',
  },
];

const Pricing = () => (
  <Section id="pricing">
    <Container>
      <AnimatedReveal>
        <div className="text-center mb-12">
          <p className="text-xs font-mono uppercase tracking-wider text-lime-500 mb-3">Pricing</p>
          <h2 className="text-2xl md:text-3xl font-display font-bold tracking-tight text-foreground mb-4">
            Get the <span className="text-lime-400">$99 Pro plan</span> for $49/mo
          </h2>

          {/* Comparison table */}
          <div className="flex justify-center gap-4 mb-6">
            <div className="px-4 py-3 rounded-lg bg-white/[0.02] border border-white/10">
              <p className="text-xs text-muted-foreground mb-1">Basic Plan (at launch)</p>
              <p className="text-xl font-bold text-foreground">$49/mo</p>
              <p className="text-xs text-muted-foreground mt-1">5 competitors • Basic features</p>
            </div>
            <div className="flex items-center">
              <ArrowRight className="h-5 w-5 text-lime-400" />
            </div>
            <div className="px-4 py-3 rounded-lg bg-lime-500/10 border border-lime-500/20">
              <p className="text-xs text-lime-400 mb-1">Pro Plan (you get this!)</p>
              <p className="text-xl font-bold text-foreground"><s className="text-muted-foreground">$99</s> $49/mo</p>
              <p className="text-xs text-lime-400 mt-1">Higher Limits • AI • Priority</p>
            </div>
          </div>

          <p className="text-base text-muted-foreground max-w-[480px] mx-auto mb-4">
            First 100 customers lock in Pro features at Basic pricing forever.
          </p>
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-lime-500/10 border border-lime-500/20">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-lime-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-lime-500"></span>
              </span>
              <p className="text-xs font-medium text-lime-400">You save $50/month forever = $600/year</p>
            </div>
            <PromoSpotsDisplay variant="pricing" />
          </div>
        </div>
      </AnimatedReveal>

      <motion.div
        className="flex justify-center max-w-md mx-auto"
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        data-testid="pricing-section"
      >
        {plans.map((plan, i) => (
          <motion.div key={i} variants={fadeInUp}>
            <div className={`rounded-2xl p-6 sm:p-7 h-full flex flex-col border relative ${
              plan.highlighted
                ? 'border-lime-500/30 bg-lime-500/[0.04] shadow-[0_0_40px_rgba(204,255,108,0.08)]'
                : 'border-white/10 bg-white/[0.02]'
            }`}>
              {plan.badge && (
                <span className="absolute -top-3 left-6 text-[10px] font-mono uppercase tracking-wider text-black bg-lime-400 px-3 py-1.5 rounded-full font-bold animate-pulse">
                  {plan.badge}
                </span>
              )}
              <h3 className="text-lg font-display font-semibold text-foreground mt-2">{plan.name}</h3>
              <div className="flex items-baseline gap-2 mt-2 mb-3">
                <span className="text-3xl font-display font-bold text-lime-400">{plan.price}</span>
                {plan.originalPrice && (
                  <span className="text-lg text-muted-foreground line-through">{plan.originalPrice}</span>
                )}
                {plan.period && <span className="text-sm text-muted-foreground">{plan.period}</span>}
              </div>
              <p className="text-sm text-muted-foreground mb-6">{plan.desc}</p>
              <ul className="space-y-3 mb-8 flex-1">
                {plan.features.map((f, j) => (
                  <li key={j} className="flex items-start gap-2.5 text-sm text-foreground/80">
                    <Check className="h-4 w-4 text-lime-500 flex-shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>
              {plan.bonuses && (
                <div className="mt-6 pt-6 border-t border-lime-500/20">
                  <p className="text-xs font-mono uppercase tracking-wider text-lime-400 mb-3">Waitlist exclusive bonuses:</p>
                  <ul className="space-y-2">
                    {plan.bonuses.map((bonus, j) => (
                      <li key={j} className="text-sm text-lime-400/90">
                        {bonus}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <Link
                to="/waitlist"
                className={`block text-center py-3 rounded-xl text-sm font-semibold transition-colors ${
                  plan.highlighted
                    ? 'bg-[#CCFF6C] text-black hover:bg-[#D9FF9A] shadow-[0_0_20px_rgba(204,255,108,0.15)]'
                    : 'bg-white/[0.04] text-foreground border border-white/10 hover:bg-white/[0.08]'
                }`}
                data-testid={`pricing-plan-${plan.name.toLowerCase()}-select-button`}
              >
                {plan.cta}
              </Link>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </Container>
  </Section>
);

/* ─── FAQ ─── */
const faqs = [
  { q: 'When will RADAR be on the Shopify App Store?', a: 'We\'re launching soon. Join the waitlist to be notified when we go live and get early access.' },
  { q: 'Is there a free trial?', a: 'No. RADAR is $49/month, billed through Shopify. You can cancel anytime through your Shopify admin.' },
  { q: 'Do you support TikTok ads?', a: 'Not yet. V1 tracks Meta (Facebook & Instagram) and Google ads. TikTok is on the roadmap for a future update.' },
  { q: 'Can I connect my own ad accounts?', a: 'Not in V1. RADAR focuses on tracking your competitors\' public ad data. Connecting your own Meta or Google ad accounts is planned for the future.' },
  { q: 'How long before I see winning ads?', a: 'You\'ll see competitor ads immediately. Winning ads are confirmed 7-14 days after a campaign launches — we need this time to see which variants survive when competitors test multiple creatives together.' },
  { q: 'Do you track inventory or stock levels?', a: 'Yes, we track product catalogs, prices, and discounts for Shopify competitors.' },
  { q: 'How many competitors can I track?', a: 'You can track 5 competitors for $49/month.' },
];

const FAQ = () => (
  <Section id="faq" className="border-y border-white/[0.04]">
    <Container>
      <AnimatedReveal>
        <div className="text-center mb-12">
          <p className="text-xs font-mono uppercase tracking-wider text-lime-500 mb-3">FAQ</p>
          <h2 className="text-2xl md:text-3xl font-display font-bold tracking-tight text-foreground">
            Frequently asked questions
          </h2>
        </div>
      </AnimatedReveal>

      <div className="max-w-2xl mx-auto">
        <Accordion type="single" collapsible className="space-y-2" data-testid="faq-accordion">
          {faqs.map((faq, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border border-white/[0.06] rounded-xl px-5 data-[state=open]:border-white/10 data-[state=open]:bg-white/[0.02]">
              <AccordionTrigger className="text-sm font-medium text-foreground hover:no-underline py-5">
                {faq.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground leading-relaxed pb-5">
                {faq.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </Container>
  </Section>
);

/* ─── FINAL CTA ─── */
const FinalCTA = () => (
  <Section>
    <Container>
      <AnimatedReveal>
        <GlassPanel className="relative overflow-hidden p-10 sm:p-16 text-center">
          <div className="absolute inset-0 bg-gradient-to-b from-lime-500/[0.04] to-transparent pointer-events-none" />
          <div className="relative z-10">
            <h2 className="text-2xl md:text-4xl font-display font-bold tracking-tight text-foreground mb-4">
              Lock in Pro features at Basic pricing forever
            </h2>
            <p className="text-base text-muted-foreground max-w-[560px] mx-auto mb-4">
              First 100 customers get the Pro plan ($99 value) for Basic pricing ($49). Plus early access to TikTok ads, ad account integration, and future platforms.
            </p>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/10 border border-red-500/20 mb-6">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
              </span>
              <PromoSpotsDisplay variant="badge" />
            </div>
            <div className="flex flex-wrap justify-center gap-3 text-xs text-muted-foreground mb-8">
              <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-lime-500" /> Pro plan at $49/mo forever</span>
              <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-lime-500" /> AI feedback on YOUR ads</span>
              <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-lime-500" /> First access to new features</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/waitlist"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-[#CCFF6C] text-black text-sm font-semibold hover:bg-[#D9FF9A] transition-colors shadow-[0_0_30px_rgba(204,255,108,0.2)]"
                data-testid="final-cta-button"
              >
                Get Pro at $49/mo
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/#pricing"
                className="inline-flex items-center gap-2 px-6 py-4 rounded-xl border border-white/10 bg-white/[0.03] text-sm font-medium text-foreground hover:bg-white/[0.06] transition-colors"
              >
                View pricing
              </Link>
            </div>
          </div>
        </GlassPanel>
      </AnimatedReveal>
    </Container>
  </Section>
);

/* ─── LANDING PAGE ─── */
export default function LandingPage() {
  return (
    <MarketingLayout>
      <SEO
        canonical="/"
        structuredData={[
          organizationSchema,
          faqSchema(faqs),
        ]}
      />
      <HeroSection />
      <WhatRadarDoes />
      <WhyMerchants />
      <CapabilityBreakdown />
      <CrossChannel />
      <ProductShowcase />
      <HowItWorks />
      <SolutionsByChannel />
      <Pricing />
      <FAQ />
      <FinalCTA />
    </MarketingLayout>
  );
}
